import { CardBody, Table, Typography } from '@statsbomb/kitbag-components';
import { KeysToCamelCase, Player, PlayerWithRels } from '@statsbomb/parachute-types';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { playerInfoAtom, unwrappedPlayerInfoAtom } from '@/atoms/player/info';
import { CardHeightWrapper } from '@/components/card/CardHeightWrapper';
import { ErrorCard } from '@/components/contentState/ErrorCard';
import { FlagIcon } from '@/components/icons/FlagIcon';
import { useFormatDob } from '@/hooks/useFormatDob';
import { useLoadableStatus } from '@/hooks/useLoadableStatus';
import { useStatus } from '@/hooks/useStatus';
import { PlayerFiltersSummary } from '../radar/PlayerFiltersSummary';

const PlayerInfoTable = ({ playerInfo }: { playerInfo?: KeysToCamelCase<Player | PlayerWithRels> | null }) => {
  const { t } = useTranslation(['playerInfo', 'countries', 'events']);
  const formatDob = useFormatDob();

  const name = playerInfo?.name || '';
  const dateOfBirth = formatDob(playerInfo) ?? '';
  const height = playerInfo?.height ? `${playerInfo.height}cm` : '';
  const preferredFoot = playerInfo?.preferredFoot
    ? t(`fields.player.preferredFoot.${playerInfo.preferredFoot.toLowerCase()}`, { ns: 'events' })
    : '';
  const nationalityAreaCode = playerInfo?.nationalityAreaCode ? (
    <span className="flex gap-2 items-center">
      <FlagIcon countryCode={playerInfo.nationalityAreaCode} />
      {t(playerInfo.nationalityAreaCode.toLowerCase(), { ns: 'countries' })}
    </span>
  ) : (
    ''
  );

  const info = [
    { key: 'name', value: name },
    { key: 'nationalityAreaCode', value: nationalityAreaCode },
    { key: 'dateOfBirth', value: dateOfBirth },
    { key: 'height', value: height },
    { key: 'preferredFoot', value: preferredFoot },
  ];

  return (
    <Table>
      <Table.Body>
        {info.map(({ key, value }) => (
          <Table.Row key={`player-info-${key}`}>
            <Table.DataCell className="w-28">{t(key, { ns: 'playerInfo' })}</Table.DataCell>
            <Table.DataCell>
              <Typography variant="headingSmall" as="span" className="text-wrap">
                {value}
              </Typography>
            </Table.DataCell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
};

const PlayerInfoBase = ({ playerInfo }: { playerInfo?: KeysToCamelCase<Player | PlayerWithRels> | null }) => (
  <div className="flex flex-col gap-2 @container">
    <PlayerInfoTable playerInfo={playerInfo} />
    <PlayerFiltersSummary />
  </div>
);

const PlayerInfoWithData = () => {
  const playerInfo = useAtomValue(unwrappedPlayerInfoAtom);
  const status = useLoadableStatus(playerInfoAtom);
  const statusProps = useStatus('general', status);

  return (
    <CardHeightWrapper>
      <CardBody {...statusProps}>
        <PlayerInfoBase playerInfo={playerInfo} />
      </CardBody>
    </CardHeightWrapper>
  );
};

export const PlayerInfo = () => (
  <ErrorCard translationNameSpace="general" fallbackChildren={<PlayerInfoBase />}>
    <PlayerInfoWithData />
  </ErrorCard>
);
